<template>
  <div class="mypage-ticket-item-block-section">
    <div class="mypage-ticket-item-block-section__header">
      <p class="mypage-ticket-item-block-section__title">{{ ticketEvent.eventName }}</p>
      <p class="mypage-ticket-item-block-section__venue">{{ displayPlaceName }}</p>
      <div class="mypage-ticket-item-block-section__date">
        <p class="mypage-ticket-item-block-section__date-start">
          <span class="mypage-ticket-item-block-section__date-data">{{
            displayEventStartDate
          }}</span>
          <span class="mypage-ticket-item-block-section__date-day">
            {{ displayEventStartDayOfWeek }}</span
          >
        </p>
        <template v-if="ticketEvent.openingDateDay2 || ticketEvent.openingDateDay3">
          <p class="mypage-ticket-item-block-section__date-div">-</p>
          <p class="mypage-ticket-item-block-section__date-end">
            <span class="mypage-ticket-item-block-section__date-data">{{
              displayEventEndDate
            }}</span>
            <span class="mypage-ticket-item-block-section__date-day">
              {{ displayEventEndDayOfWeek }}</span
            >
          </p>
        </template>
      </div>
      <div class="mypage-ticket-item-block-section__opening">
        <p class="mypage-ticket-item-block-section__opening-title">
          {{ $tc('MypagePage.MypageTicketListPage.openingTitle') }}
        </p>
        <div class="mypage-ticket-item-block-section__opening-body">
          <table class="mypage-ticket-item-block-section__opening-table">
            <tr
              v-for="(date, index) in displayOpeningDates"
              :key="`${ticketEvent.uniqueKey}-${index}`"
              class="mypage-ticket-item-block-section__opening-data"
            >
              <template v-if="date.value">
                <!-- 通しイベント場合に「x日目」を表示 -->
                <th
                  v-if="ticketEvent.openingDateDay2"
                  class="mypage-ticket-item-block-section__opening-data-date"
                >
                  {{ getOpeningDate(date.value) }}
                </th>
                <td class="mypage-ticket-item-block-section__opening-data-time">
                  {{ getOpeningTime(date.value) }}
                </td>
              </template>
            </tr>
          </table>
        </div>
      </div>
    </div>

    <div
      v-if="isExpanded && purchasedTicketTypes && purchasedTicketTypes.length > 0"
      class="mypage-ticket-item-block-section__body"
    >
      <ul class="mypage-ticket-item-block-section__ticket-list">
        <li
          v-for="(ticketType, index) in purchasedTicketTypes"
          :key="index"
          class="mypage-ticket-item-block-section__ticket-item"
        >
          <mypage-ticket-item-section
            :ticketType="ticketType"
            :purchasedTicketInfo="purchasedTicketInfo"
          />
        </li>
      </ul>
    </div>

    <div class="mypage-ticket-item-block-section__toggle">
      <button
        class="mypage-ticket-item-block-section__toggle-button"
        :class="{ 'mypage-ticket-item-block-section__toggle-button--open': isExpanded }"
        @click="handleToggleClicked"
      >
        <span class="mypage-ticket-item-block-section__toggle-button-label">{{
          toggleButtonLabel
        }}</span>
        <span class="mypage-ticket-item-block-section__toggle-button-icon">
          <mypage-ticket-accordion-icon-parts />
        </span>
      </button>
    </div>

    <div class="mypage-ticket-item-block-section__footer" v-if="hasPastTickets">
      <template v-for="(date, index) in displayOpeningDates">
        <div
          :key="index"
          v-if="isPastTicket(date)"
          class="mypage-ticket-item-block-section__footer-ticket"
          @click="moveToMypageTicketPastPage(date.key)"
        >
          <atom-router-button to="" :linkText="getPastButtonText(index)" colorType="secondary" />
        </div>
      </template>
    </div>

    <div class="mypage-ticket-item-block-section__circuit">
      <p class="mypage-ticket-item-block-section__circuit-icon">
        <motegi-parts v-if="ticketEvent.stadiumId === 'TWIN_RING_MOTEGI'" />
        <fuji-parts v-if="ticketEvent.stadiumId === 'FUJI_SPEEDWAY'" />
        <sugo-parts v-if="ticketEvent.stadiumId === 'SPORTSLAND_SUGO'" />
        <autopolice-parts v-if="ticketEvent.stadiumId === 'AUTOPOLIS'" />
        <suzuka-parts v-if="ticketEvent.stadiumId === 'SUZUKA_CIRCUIT'" />
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, inject, PropType, ref, Ref } from '@vue/composition-api'
import VueRouter from 'vue-router'
import { orderBy } from 'lodash'
import dayjs from 'dayjs'
import I18n from '@/locales/I18n'
import MypageTicketAccordionIconParts from '@/components/MypageTicketListPage/MypageTicketListPane/parts/MypageTicketAccordionIconParts.vue'
import MypageTicketItemSection from '@/components/MypageTicketListPage/MypageTicketListPane/MypageTicketItemSection.vue'
import AtomRouterButton from '@/components/atoms/AtomRouterButton.vue'
import MotegiParts from '@/components/common/circuit/MotegiParts.vue'
import FujiParts from '@/components/common/circuit/FujiParts.vue'
import SugoParts from '@/components/common/circuit/SugoParts.vue'
import AutopoliceParts from '@/components/common/circuit/AutopoliceParts.vue'
import SuzukaParts from '@/components/common/circuit/SuzukaParts.vue'
import DigitalTicketDataDocument, {
  OpeningDatesType,
} from '@/store/stores/collectionModule/documents/digitalTicket/DigitalTicketDataDocument'
import DigitalTicketDataTicketTypeDocument from '@/store/stores/collectionModule/documents/digitalTicket/DigitalTicketDataTicketTypeDocument'
import StoreUtil from '@/store/StoreUtil'
import useDisplayDependingOnLang from '@/components/hook/useDisplayDependingOnLang'
import usePurchasedTicketData, {
  UserPurchasedTicketType,
} from '@/components/hook/ticket/usePurchasedTicketData'
import useDisplayTicketData from '@/components/MypageTicketListPage/hook/useDisplayTicketData'

type OpeningDateDataType = {
  key: OpeningDatesType
  value: number
}

/**
 * マイページ: チケットブロックセクション
 * イベントとイベントに紐づくチケット一覧を1ブロックとして扱う
 */
export default defineComponent({
  name: 'MypageTicketBlockSection',
  components: {
    SuzukaParts,
    AutopoliceParts,
    SugoParts,
    FujiParts,
    MotegiParts,
    AtomRouterButton,
    MypageTicketItemSection,
    MypageTicketAccordionIconParts,
  },
  inject: ['router'],
  props: {
    /**
     * デジタルチケット イベント情報
     */
    ticketEvent: {
      type: Object as PropType<DigitalTicketDataDocument>,
      required: true,
    },
  },
  setup(props) {
    // store
    const mypageTicketPageStore = StoreUtil.useStore('MypageTicketPageStore')
    const mypagePageStore = StoreUtil.useStore('MypagePageStore')
    const { selectedPastTicketInfo } = mypageTicketPageStore
    const { ownOrganization } = mypagePageStore
    // hook
    const { getDisplayPlaceName } = useDisplayTicketData()
    const { userPurchasedTicket } = usePurchasedTicketData()
    const { getDisplayDateJa } = useDisplayDependingOnLang()

    const router = inject('router') as VueRouter

    // 詳細を開くかどうか
    const isExpanded = ref(false)

    // 開閉ボタンのラベルを返す
    const toggleButtonLabel = computed(() =>
      isExpanded.value
        ? I18n.tc('MypagePage.MypageTicketListPage.toggleLabel.close')
        : I18n.tc('MypagePage.MypageTicketListPage.toggleLabel.open'),
    )

    /**
     * 場所
     */
    const displayPlaceName = computed(() => getDisplayPlaceName(props.ticketEvent))

    /**
     * 開場日時をkey(n日目)、value(開場日時)の配列に変換したデータ
     */
    const displayOpeningDates: Ref<Array<OpeningDateDataType>> = computed(() =>
      orderBy(
        Object.entries(props.ticketEvent.openingDates ?? {}).map(([key, value]) => ({
          key: key as OpeningDatesType,
          value,
        })),
        'key',
      ),
    )

    /**
     * イベント期間開始日
     */
    const displayEventStartDate = computed(() =>
      getDisplayDateJa(props.ticketEvent.openingDates?.day1),
    )

    /**
     * イベント期間開始日の曜日
     */
    const displayEventStartDayOfWeek = computed(() =>
      getDisplayDateJa(props.ticketEvent.openingDates?.day1, 'ddd', 'ddd'),
    )

    /**
     * イベント期間終了日
     */
    const displayEventEndDate = computed(() => {
      const endDate = orderBy(displayOpeningDates.value, 'key', 'desc').find(
        (date) => date.value,
      )?.value
      return getDisplayDateJa(endDate)
    })

    /**
     * イベント期間終了日の曜日
     */
    const displayEventEndDayOfWeek = computed(() => {
      const endDate = orderBy(displayOpeningDates.value, 'key', 'desc').find(
        (date) => date.value,
      )?.value
      return getDisplayDateJa(endDate, 'ddd', 'ddd')
    })

    /**
     * ログインユーザーが購入した商品情報
     */
    const purchasedTicketInfo = computed(() =>
      userPurchasedTicket([props.ticketEvent], ownOrganization.value),
    )

    /**
     * ログインユーザーが購入したチケットに紐づく券種データ
     */
    const purchasedTicketTypes = computed(() =>
      orderBy(props.ticketEvent.ticketTypes, 'order')?.filter((ticketType) =>
        purchasedTicketInfo.value.ticketTypeIds.all.some(
          (ticketTypeId) => ticketTypeId === ticketType.ticketTypeId,
        ),
      ),
    )

    /**
     * 開場日を取得する
     */
    const getOpeningDate = (date: number) =>
      `${getDisplayDateJa(date, 'MM/DD', 'MMM DD')} ${getDisplayDateJa(date, 'ddd', 'ddd')}`

    /**
     * 開場時間を取得する
     */
    const getOpeningTime = (date: number) => getDisplayDateJa(date, 'HH:mm', 'HH:mm')

    /**
     * 過去のチケットを表示するボタンのテキスト
     */
    const getPastButtonText = (index: number) =>
      I18n.t('MypagePage.MypageTicketListPage.pastTicketButtonLabel', {
        day: index + 1,
      }).toString()

    /** 今日の0:00:00 */
    const beginningOfTheDay = dayjs().startOf('day').valueOf()

    /**
     * 過去のチケットかどうか
     */
    const isPastTicket = (openingDateInfo: OpeningDateDataType) => {
      // 対象日に利用可能なチケットを持っているか
      const hasTargetDateTickets = purchasedTicketTypes.value?.some((ticketType) =>
        ticketType.tickets?.some((ticket) => {
          const isPurchased = purchasedTicketInfo.value.productNumbers.all.some(
            (productNumber) => productNumber === ticket.productNumber,
          )
          if (!isPurchased) {
            return false
          }
          return ticket.availableDates?.[openingDateInfo.key]
        }),
      )
      if (!hasTargetDateTickets) {
        // 対象日に利用可能なチケットを購入していない場合はボタンを表示しない
        return false
      }
      if (!openingDateInfo.value || beginningOfTheDay <= openingDateInfo.value) {
        // イベントの開催日が当日以降のチケットは表示しない
        return false
      }

      /**
       * 対象イベントの開場日に紐づく有効なチケットを持っている場合、ボタンを表示する
       * 以下で利用しているメソッドは対象イベントの開場日に紐づく当日以降に使えるチケット情報も取得できてしまうが、
       * 上記「イベントの開催日が今日以降のチケットは表示しない」の処理で当日以降のイベントの場合はこちらの処理に入らないように制御されている。
       */
      return purchasedTicketInfo.value.productNumbers.all.length > 0
    }

    /**
     * 過去のチケットが存在するかどうか
     */
    const hasPastTickets = computed(() =>
      displayOpeningDates.value.some((date) => isPastTicket(date)),
    )

    // 開閉ボタンが押下されたときの処理
    const handleToggleClicked = () => {
      isExpanded.value = !isExpanded.value
    }

    /**
     * 過去のチケットページに遷移する
     */
    const moveToMypageTicketPastPage = (targetDay: OpeningDatesType) => {
      // 表示対象のイベントとイベントの開催日目をストアに保存
      selectedPastTicketInfo.value = {
        ticketEventUniqueKey: props.ticketEvent.uniqueKey,
        day: targetDay,
      }
      router.replace('/mypage/ticket-past')
    }

    return {
      isExpanded,
      toggleButtonLabel,
      displayPlaceName,
      displayOpeningDates,
      displayEventStartDate,
      displayEventStartDayOfWeek,
      displayEventEndDate,
      displayEventEndDayOfWeek,
      purchasedTicketInfo: purchasedTicketInfo as Ref<UserPurchasedTicketType>,
      purchasedTicketTypes: purchasedTicketTypes as Ref<
        Array<DigitalTicketDataTicketTypeDocument> | undefined
      >,
      getOpeningDate,
      getOpeningTime,
      getPastButtonText,
      isPastTicket,
      hasPastTickets,
      handleToggleClicked,
      moveToMypageTicketPastPage,
    }
  },
})
</script>

<style scoped lang="scss">
.mypage-ticket-item-block-section {
  position: relative;
  padding: 16px 16px 10px;
  overflow: hidden;
  background-color: $white;
  border-bottom: 1px solid $black10;

  &__header {
    position: relative;
    z-index: 2;
  }

  &__title {
    font-size: 18px;
    font-weight: 600;
    color: $black80;
    letter-spacing: 0.04em;
  }

  &__venue {
    font-size: 12px;
    color: $gray75;
  }

  &__date {
    display: flex;
    gap: 7px;
    color: $black20;

    &-data {
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0.04em;
    }

    &-day {
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.04em;
    }

    &-div {
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.04em;
    }
  }

  &__opening {
    display: flex;

    &-title {
      font-size: 12px;
      font-weight: 500;
      color: $gray75;
    }

    &-body {
      margin-left: 14px;
      font-size: 12px;
      font-weight: 500;
      color: $black20;
    }

    &-data {
      position: relative;
      letter-spacing: 0.04em;

      &-date {
        width: 80px;
        padding-right: 4px;
        white-space: nowrap;
      }

      &-time {
        position: relative;
        width: 54px;
        text-align: right;

        &::before {
          position: absolute;
          top: 50%;
          left: 0;
          width: 2px;
          height: 8px;
          content: '';
          background: linear-gradient(
            84.94deg,
            #00ffba -121.14%,
            #00ffba -121.14%,
            rgba(0, 255, 186, 0.5) -80.24%,
            #00ffba -34.6%,
            rgba(0, 255, 186, 0.5) 10.88%,
            #00ffba 51.93%,
            rgba(0, 255, 186, 0.5) 91.87%
          );
          transform: translateY(-50%);
        }
      }
    }
  }

  &__body {
    margin-top: 16px;
  }

  &__ticket {
    &-item {
      + .mypage-ticket-item-block-section__ticket-item {
        margin-top: 16px;
      }
    }
  }

  &__toggle {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: flex-end;

    .mypage-ticket-item-block-section__body + & {
      height: 24px;
      margin-top: 16px;
    }

    &-button {
      display: inline-flex;
      gap: 4px;
      align-items: center;
      padding: 0 16px;
      font-size: 10px;
      font-weight: 500;
      color: $gray75;
      letter-spacing: 0.04em;
      line-height: 1.4;

      &-icon {
        width: 16px;
        height: 16px;
      }

      &--open {
        .mypage-ticket-item-block-section__toggle-button-icon {
          transform: rotate(-180deg);
        }
      }
    }
  }

  &__footer {
    position: relative;
    z-index: 2;
    padding-bottom: 6px;

    @media screen and (min-width: $responsiveTabletWidth - 1px) {
      display: flex;
      gap: 8px;
      align-items: center;
      justify-content: center;
      height: 80px;
      margin-top: 23px;
    }

    &-ticket {
      margin-top: 16px;

      @media screen and (min-width: $responsiveTabletWidth - 1px) {
        width: 347px;
        margin-top: 0;
      }
    }
  }

  &__circuit {
    position: absolute;
    top: 16px;
    right: 8px;
    z-index: 1;
    opacity: 0.03;
    color: $black;

    &-icon {
      display: flex;
      justify-content: center;
      width: 150px;
      height: 114px;
    }
  }
}
</style>
